import { useState } from "react";
import { useRouter } from "next/router";
import { useSetRecoilState } from "recoil";
import { isLoggedInState } from "@/atoms/accountState";
import { Axios } from "@/utils/axios";
import { HackleClient } from "@/utils/hackle";
import { naverConversionEvent } from "@/utils/naverPageView";
import {
  cooKeys,
  customEvent,
  getCookie,
  getUserProperties,
  HackleEventName,
  removeCookie,
  setCookie,
} from "goi_common";
import { 장례준비긴급도_LIST_EVENT_KEY } from "@/containers/MatchContainer/constants/request";
import {
  SessionLandingType,
  랜딩페이지_LIST_EVENT_KEY,
  랜딩페이지_LIST_EVENT_KEYS,
} from "@/constants/landingType";

const KAKAO_CLIENT_ID = "778e49a4ec757aa2a95c8e1d48f45e91";

/** 카카오 인증 페이지 주소. 훅 밖(E5 받는 방법 등)에서도 같은 키 · 같은 주소를 쓰게 내보낸다 */
export const kakaoAuthorizeUrl = (redirectUri = `${location.origin}/login/`) =>
  `https://kauth.kakao.com/oauth/authorize?response_type=code&through_account=true&client_id=${KAKAO_CLIENT_ID}&prompt=login&redirect_uri=${redirectUri}`;

export type LoginErrorType = "already_signed_up" | "dormant" | "generic";

interface UseKakaoLoginOptions {
  /** 로그인 성공 후 이동할 경로. 미지정 시 kakao_redirect 쿠키 → "/" 순서로 사용 */
  redirectTo?: string;
  /** 성공 콜백 (리다이렉트 전 추가 처리용) */
  onSuccess?: (res: any) => void;
  /** 실패 콜백 */
  onFailure?: (errorType: LoginErrorType, err: any) => void;
}

/**
 * 카카오 로그인 핵심 로직을 담은 훅.
 *
 * - 카카오 인증 페이지 이동 (handleKakaoAuth)
 * - 인증 코드로 로그인 API 호출 (handleKakaoCallback)
 * - Recoil 상태 + 쿠키 + localStorage 업데이트
 * - 이벤트 트래킹 (GTM, Hackle, Naver)
 * - 리다이렉트 처리
 */
export function useKakaoLogin(options: UseKakaoLoginOptions = {}) {
  const router = useRouter();
  const setIsLoggedIn = useSetRecoilState(isLoggedInState);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<LoginErrorType | null>(null);

  const getRedirectTo = () => {
    if (options.redirectTo) return options.redirectTo;
    return getCookie(cooKeys.kakao_redirect) || "/";
  };

  /** 카카오 인증 페이지로 이동 */
  const handleKakaoAuth = (redirectUri?: string) => {
    location.href = kakaoAuthorizeUrl(redirectUri || undefined);
  };

  /** 로그인 성공 처리 (상태 업데이트 + 이벤트 + 리다이렉트) */
  const processLoginSuccess = async (res: any) => {
    if (res.status !== 200 && res.status !== 201) return;

    const event_from = getCookie(cooKeys.event_from);

    setIsLoggedIn(true);

    const {
      data: { memberId },
    } = await Axios.get("/users/init/");

    const id = "goi-" + memberId;
    localStorage.setItem("goi-member-id", id);
    setCookie(cooKeys.goi_member_id, id);

    HackleClient.setUser({
      userId: id,
      properties: getUserProperties(),
    });

    customEvent({ newGtm: { action: "signup_or_login_success" } });

    if (res.status === 201) {
      naverConversionEvent({ type: "sign_up" });

      const quotation = getCookie(cooKeys.quotation_request);
      if (quotation?.status) {
        const statusEventKey = 장례준비긴급도_LIST_EVENT_KEY[quotation.status];
        customEvent({ newGtm: { action: `signup_status_${statusEventKey}` } });
        /**
         * 광고 전환가치용 리드 제출 이벤트. 견적 퍼널에서 signup 이 곧 리드 제출이다.
         * `장례준비긴급도_LIST_EVENT_KEY` 는 `Record<string, ...>` 이라 키 미스가 타입으로 안 잡힌다.
         * 실측상 `signup_status_undefined` 는 6개월간 0건이지만, 미스가 나면 구글에 없는 전환
         * 이름이라 그 리드의 전환값이 통째로 소실되므로 v2 쪽과 같은 unknown 으로 떨어뜨린다.
         */
        customEvent({ newGtm: { action: `goi_lead_${statusEventKey ?? "unknown"}` } });

        const isSearchAd = getCookie(cooKeys.is_search_ad);
        if (isSearchAd) {
          customEvent({ newGtm: { action: "signup_search_needs" } });
        }

        const sessionLandingType = getCookie(cooKeys.session_landing_type);
        if (sessionLandingType && 랜딩페이지_LIST_EVENT_KEYS.includes(sessionLandingType)) {
          customEvent({
            newGtm: {
              action: `signup_landing_${
                랜딩페이지_LIST_EVENT_KEY[sessionLandingType as SessionLandingType]
              }`,
            },
          });
        }

        const 긴급도_VALUES = Object.values(장례준비긴급도_LIST_EVENT_KEY).filter(
          (v) => v !== "notyet",
        );
        if (긴급도_VALUES.includes(statusEventKey)) {
          naverConversionEvent({ type: "lead" });
        }
      }

      customEvent({
        newGtm: { action: "signup" },
        hackle: { key: HackleEventName.회원가입 },
      });

      if (event_from) {
        customEvent({
          newGtm: { path: "login", trigger_and_target: `signup_${event_from}` },
        });
      }
    } else {
      customEvent({ newGtm: { action: "login" }, triggerHackle: true });

      if (event_from) {
        customEvent({
          newGtm: { path: "login", trigger_and_target: `login_${event_from}` },
        });
      }
    }

    options.onSuccess?.(res);

    // 리다이렉트
    const to = getRedirectTo();
    removeCookie(cooKeys.kakao_redirect);
    removeCookie(cooKeys.event_from);

    // code 쿼리 파라미터가 있으면 제거하면서 리다이렉트
    router.replace(to);
  };

  /** 로그인 실패 처리 */
  const processLoginFailure = (err: any) => {
    let errorType: LoginErrorType = "generic";

    if (err.response?.data === "Social account with given phone number exists.") {
      errorType = "already_signed_up";
    } else if (err.response?.status === 403) {
      errorType = "dormant";
    }

    setError(errorType);
    options.onFailure?.(errorType, err);
  };

  /** 카카오 인증 코드로 로그인 API 호출. redirectUri는 code 발급 시 사용한 redirect_uri와 일치해야 함 */
  const handleKakaoCallback = (code: string, redirectUri?: string) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setError(null);

    const quotationRequest = getCookie(cooKeys.quotation_request);
    const facilityQuotationRequestId = getCookie(cooKeys.quotation_request_facility);

    const memo = `From: ${
      router.asPath
    }, To: ${getRedirectTo()} \n 주요 방문 기능(최근부터): ${getCookie(cooKeys.visited_feature)}`;

    Axios.post("/users/kakao_login/", {
      authorization_code: code,
      memo,
      quotation_request_id: quotationRequest?.id,
      jangji_recommend_request_id: Number(facilityQuotationRequestId) || null,
      ...(redirectUri ? { redirect_uri: redirectUri } : {}),
    })
      .then((res) => processLoginSuccess(res))
      .catch((err) => processLoginFailure(err))
      .finally(() => {
        setIsProcessing(false);
        removeCookie(cooKeys.kakao_auto_login_handled);
      });
  };

  /**
   * 번호(비카카오) 로그인·가입. `/users/easy_login/`.
   *
   * 로그인 페이지의 "카카오톡 없이 계속 이용하기"와 같은 엔드포인트다. 성공 처리
   * (상태·쿠키·Hackle·이벤트·리다이렉트)는 카카오와 완전히 같아서 `processLoginSuccess`
   * 를 그대로 공유한다. 이 훅이 카카오 전용 이름인 건 역사적 이유이고, 실제로는
   * "소셜/번호 로그인 성공 처리"를 소유한다.
   *
   * 가입 방식 차원(`signup_easy`)은 여기서 추가 발화한다. `signup` 은
   * `processLoginSuccess` 가 이미 쏘므로 중복되지 않는다.
   * (참고: wiki/features/platform-signup-method-events.md)
   *
   * @param telephone 하이픈 포함 번호. `checkPhoneNumber` 로 검증한 값을 넘길 것
   * @returns 성공/실패 처리까지 끝나는 Promise. 호출부는 로딩 해제에만 쓰면 된다
   */
  const handleEasyLogin = (telephone: string) => {
    if (isProcessing) return Promise.resolve();
    setIsProcessing(true);
    setError(null);

    const quotationRequest = getCookie(cooKeys.quotation_request);
    const facilityQuotationRequestId = getCookie(cooKeys.quotation_request_facility);

    const memo = `From: ${
      router.asPath
    }, To: ${getRedirectTo()} \n 주요 방문 기능(최근부터): ${getCookie(cooKeys.visited_feature)}`;

    return Axios.post("/users/easy_login/", {
      telephone,
      memo,
      quotation_request_id: quotationRequest?.id,
      jangji_recommend_request_id: Number(facilityQuotationRequestId) || null,
    })
      .then((res) => {
        if (res.status === 201) {
          customEvent({ newGtm: { action: "signup_easy" } });
        }
        return processLoginSuccess(res);
      })
      .catch((err) => processLoginFailure(err))
      .finally(() => setIsProcessing(false));
  };

  return {
    handleKakaoAuth,
    handleKakaoCallback,
    handleEasyLogin,
    isProcessing,
    error,
  };
}
